/**
 * 會計(REQ-20260806002)—— 四大模組的入口。
 *
 * 出貨管理 / 進貨管理 / 應收沖帳 / 應付沖帳,四個分頁共用同一份主檔連動元件
 * (PartyPicker),所以「輸入編號自動帶入」的行為在四個地方完全一致。
 *
 * 分頁狀態仍然是本地 state,只多讀一次網址上的 `tab` / `editOrderId`
 * (REQ-20260811001)—— 讓別的畫面(訪談的重複建檔提醒)能直接連過來開某張單的
 * 編輯表單。刻意不做成受控於 URL 的完整路由:切分頁不寫回網址,只要「進得來」。
 *
 * REQ-20260820006 把上面那句話的後半段推翻了:**切分頁現在會寫回網址**。
 * 當時只有「進得來」這一個需求,現在多了「回得來」—— 清單的篩選條件也搬到網址上
 * (見 SalesOrdersPanel / PurchaseOrdersPanel),而列印頁「關閉頁面」導回來的
 * 那個網址必須說得出「哪個分頁 + 哪組篩選」。
 * 但只多這一件事:分頁本身仍是本地 state,**沒有**改成 react-router 巢狀路由。
 *
 * REQ-20260815001 用同一套模式多收 `customerId` / `orderNo`,讓訪談上的
 * 「收款」徽章能連到應收沖帳並把客戶與那張單都先備妥。
 *
 * REQ-20260815009 再多一組 `shipOrderId` / `orderNo`,讓訪談的出貨徽章與儀表板的
 * 未出貨清單能直接開那張單的**出貨登記彈窗**(不是編輯表單)。
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReportsPanel } from '../features/ar-ap-reports/ReportsPanel';
import { FreightConsolidationPanel } from '../features/freight-lines/FreightConsolidationPanel';
import { PurchaseOrdersPanel } from '../features/purchase-orders/PurchaseOrdersPanel';
import { SalesOrdersPanel } from '../features/sales-orders/SalesOrdersPanel';
import { PayablePanel } from '../features/statements/PayablePanel';
import { ReceivablePanel } from '../features/statements/ReceivablePanel';

const TABS = [
  { key: 'sales', label: '出貨管理' },
  { key: 'purchase', label: '進貨管理' },
  { key: 'receivable', label: '應收沖帳' },
  { key: 'payable', label: '應付沖帳' },
  // 月底把同一家貨運行的運費明細彙整成一張進貨單(REQ-20260908002)。
  // 排在應付沖帳之後:它產生的是**草稿**進貨單,還沒有應付可以沖
  { key: 'freight', label: '運費歸單' },
  // 舊 PC 會計系統「應收付款」選單裡的那一疊報表(REQ-20260815002)。
  // 應收 / 應付各一個分頁,底下再用下拉挑報表 —— 九張報表各給一個分頁會把上面四個擠爆。
  { key: 'ar-reports', label: '應收報表' },
  { key: 'ap-reports', label: '應付報表' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export function AccountingPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TABS.find((t) => t.key === searchParams.get('tab'))?.key ?? 'sales';
  const [tab, setTab] = useState<TabKey>(initialTab);
  /**
   * 深連結指定的「直接開這張單的編輯表單」。使用者手動切過分頁之後就清掉 ——
   * 否則切走再切回來,編輯表單會莫名其妙又跳出來一次。
   */
  const [deepLinkOrderId, setDeepLinkOrderId] = useState<number | undefined>(() => {
    if (initialTab !== 'sales') return undefined;
    const id = Number(searchParams.get('editOrderId'));
    return Number.isInteger(id) && id > 0 ? id : undefined;
  });
  /**
   * 深連結指定的「幫我把這個客戶的這張單準備好收款」(REQ-20260815001)。
   * 讀法與清除時機都跟 `deepLinkOrderId` 同一套,只是換成應收沖帳分頁。
   */
  const [deepLinkReceipt, setDeepLinkReceipt] = useState<
    { customerId: number | undefined; orderNo: string | undefined } | undefined
  >(() => {
    if (initialTab !== 'receivable') return undefined;
    const customerId = Number(searchParams.get('customerId'));
    return {
      customerId: Number.isInteger(customerId) && customerId > 0 ? customerId : undefined,
      orderNo: searchParams.get('orderNo') ?? undefined,
    };
  });
  /**
   * 深連結指定的「直接開這張單的出貨登記彈窗」(REQ-20260815009)。
   * 出貨彈窗的標題要顯示單號,而列表可能還沒載完(或那張單不在目前的篩選結果裡),
   * 所以單號跟著網址一起帶進來 —— 兩個都有值才算數,少一個就當作沒帶。
   */
  const [deepLinkShipment, setDeepLinkShipment] = useState<
    { orderId: number; orderNo: string } | undefined
  >(() => {
    if (initialTab !== 'sales') return undefined;
    const orderId = Number(searchParams.get('shipOrderId'));
    const orderNo = searchParams.get('orderNo');
    if (!Number.isInteger(orderId) || orderId <= 0 || !orderNo) return undefined;
    return { orderId, orderNo };
  });

  return (
    <section className="grid gap-4">
      <h1 className="text-xl font-semibold no-print">會計</h1>

      <div className="border-b flex gap-1 no-print" role="tablist" aria-label="會計模組">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => {
              setTab(t.key);
              /*
               * 分頁寫回網址(REQ-20260820006)。**只留 tab**:上一個分頁的篩選條件
               * 與已經吃掉的深連結參數都不該跟著換頁走(那三個 state 也在下面清掉,
               * 是同一件事的兩半)。用 replace —— 切分頁不該塞一筆瀏覽紀錄,
               * 不然「上一頁」會變成在分頁之間跳。
               */
              setSearchParams({ tab: t.key }, { replace: true });
              setDeepLinkOrderId(undefined);
              setDeepLinkReceipt(undefined);
              setDeepLinkShipment(undefined);
            }}
            className={`px-3 py-2 text-sm rounded-t focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
              tab === t.key
                ? 'border-b-2 border-blue-600 text-blue-700 font-medium'
                : 'text-slate-600 hover:text-blue-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'sales' ? (
        <SalesOrdersPanel
          initialEditOrderId={deepLinkOrderId}
          initialShipOrderId={deepLinkShipment?.orderId}
          initialShipOrderNo={deepLinkShipment?.orderNo}
        />
      ) : null}
      {tab === 'purchase' ? <PurchaseOrdersPanel /> : null}
      {tab === 'receivable' ? (
        <ReceivablePanel
          initialCustomerId={deepLinkReceipt?.customerId}
          initialOrderNo={deepLinkReceipt?.orderNo}
        />
      ) : null}
      {tab === 'payable' ? <PayablePanel /> : null}
      {tab === 'freight' ? <FreightConsolidationPanel /> : null}
      {tab === 'ar-reports' ? <ReportsPanel side="ar" /> : null}
      {tab === 'ap-reports' ? <ReportsPanel side="ap" /> : null}
    </section>
  );
}
